/* Native Currency Converter — replaces BEAST/doubly
 * Self-contained vanilla JS, no jQuery, no external app dependency.
 * Reads config from a global `window.__NCC_CONFIG` object emitted by Liquid.
 * Uses `window.Currency.rates` (from assets/currency-rates.js) for conversion.
 */
(function () {
  'use strict';

  var CONFIG = window.__NCC_CONFIG || {};
  if (!CONFIG.enabled) return;

  var SHOP = (CONFIG.shopCurrency || 'USD').toUpperCase();
  var ALLOWED = (CONFIG.allowedCurrencies || 'USD,EUR,GBP,CAD,AUD').toUpperCase()
    .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  var COOKIE_NAME = CONFIG.cookieName || 'ncc_currency';
  var SPAN_CLASS = CONFIG.spanClass || 'money';
  var SHOW_CODE = !!CONFIG.showCurrencyCode;
  var REMOVE_DECIMALS = !!CONFIG.removeDecimals;
  var ROUND_DECIMALS = !!CONFIG.roundDecimals;
  var ROUND_TO = (CONFIG.roundTo == null) ? '99' : String(CONFIG.roundTo);
  var SHOW_NOTICE = !!CONFIG.showCheckoutNotice;
  var NOTICE_TEXT = CONFIG.checkoutNoticeText || 'Note: orders are processed in [shop_currency]. Your card will be charged in [shop_currency].';
  var SHOW_PRICE_ON_HOVER = !!CONFIG.showPriceOnHover;
  var HIDE_SWITCHER = !!CONFIG.hideSwitcher;
  var AUTO_DETECT = (CONFIG.autoDetect !== false);
  var GEO_ENDPOINT = CONFIG.geoEndpoint || 'https://ipapi.co/json/';

  /* ---------- Money format templates per currency ---------- */
  var FORMATS = {
    USD: { sym: '$', code: 'USD', tpl: '${{amount}}' },
    EUR: { sym: '€', code: 'EUR', tpl: '€{{amount_with_comma_separator}}' },
    GBP: { sym: '£', code: 'GBP', tpl: '£{{amount}}' },
    CAD: { sym: '$', code: 'CAD', tpl: '${{amount}}' },
    AUD: { sym: '$', code: 'AUD', tpl: '${{amount}}' },
    NZD: { sym: '$', code: 'NZD', tpl: '${{amount}}' },
    JPY: { sym: '¥', code: 'JPY', tpl: '¥{{amount_no_decimals}}' },
    CNY: { sym: '¥', code: 'CNY', tpl: '¥{{amount}}' },
    CHF: { sym: 'CHF ', code: 'CHF', tpl: 'CHF {{amount_with_comma_separator}}' },
    SEK: { sym: 'kr ', code: 'SEK', tpl: 'kr {{amount_with_comma_separator}}' },
    NOK: { sym: 'kr ', code: 'NOK', tpl: 'kr {{amount_with_comma_separator}}' },
    DKK: { sym: 'kr.', code: 'DKK', tpl: 'kr. {{amount_with_comma_separator}}' },
    PLN: { sym: 'zł ', code: 'PLN', tpl: '{{amount_with_comma_separator}} zł' },
    CZK: { sym: 'Kč ', code: 'CZK', tpl: '{{amount_with_comma_separator}} Kč' },
    HUF: { sym: 'Ft ', code: 'HUF', tpl: '{{amount_no_decimals_with_comma_separator}} Ft' },
    RON: { sym: 'lei ', code: 'RON', tpl: '{{amount_with_comma_separator}} lei' },
    BGN: { sym: 'лв ', code: 'BGN', tpl: '{{amount_with_comma_separator}} лв' },
    RUB: { sym: '₽ ', code: 'RUB', tpl: '{{amount_with_comma_separator}} ₽' },
    UAH: { sym: '₴ ', code: 'UAH', tpl: '{{amount_with_comma_separator}} ₴' },
    TRY: { sym: '₺', code: 'TRY', tpl: '₺{{amount_with_comma_separator}}' },
    INR: { sym: '₹', code: 'INR', tpl: '₹{{amount}}' },
    PKR: { sym: 'Rs ', code: 'PKR', tpl: 'Rs {{amount}}' },
    BDT: { sym: 'Tk ', code: 'BDT', tpl: 'Tk {{amount}}' },
    LKR: { sym: 'Rs ', code: 'LKR', tpl: 'Rs {{amount}}' },
    NPR: { sym: 'Rs ', code: 'NPR', tpl: 'Rs {{amount}}' },
    SGD: { sym: 'S$', code: 'SGD', tpl: 'S${{amount}}' },
    HKD: { sym: 'HK$', code: 'HKD', tpl: 'HK${{amount}}' },
    TWD: { sym: 'NT$', code: 'TWD', tpl: 'NT${{amount}}' },
    KRW: { sym: '₩', code: 'KRW', tpl: '₩{{amount_no_decimals}}' },
    THB: { sym: '฿', code: 'THB', tpl: '฿{{amount}}' },
    MYR: { sym: 'RM ', code: 'MYR', tpl: 'RM {{amount}}' },
    IDR: { sym: 'Rp ', code: 'IDR', tpl: 'Rp {{amount_no_decimals_with_comma_separator}}' },
    PHP: { sym: '₱', code: 'PHP', tpl: '₱{{amount}}' },
    VND: { sym: '₫', code: 'VND', tpl: '{{amount_no_decimals_with_comma_separator}} ₫' },
    AED: { sym: 'AED ', code: 'AED', tpl: 'AED {{amount}}' },
    SAR: { sym: 'SAR ', code: 'SAR', tpl: 'SAR {{amount}}' },
    QAR: { sym: 'QAR ', code: 'QAR', tpl: 'QAR {{amount}}' },
    KWD: { sym: 'KWD ', code: 'KWD', tpl: 'KWD {{amount}}' },
    BHD: { sym: 'BD ', code: 'BHD', tpl: '{{amount}} BD' },
    OMR: { sym: 'OMR ', code: 'OMR', tpl: 'OMR {{amount}}' },
    JOD: { sym: 'JOD ', code: 'JOD', tpl: 'JOD {{amount}}' },
    ILS: { sym: '₪', code: 'ILS', tpl: '₪{{amount}}' },
    EGP: { sym: 'E£', code: 'EGP', tpl: 'E£{{amount}}' },
    MAD: { sym: 'MAD ', code: 'MAD', tpl: '{{amount_with_comma_separator}} dh' },
    ZAR: { sym: 'R ', code: 'ZAR', tpl: 'R {{amount}}' },
    NGN: { sym: '₦ ', code: 'NGN', tpl: '₦{{amount}}' },
    KES: { sym: 'KSh ', code: 'KES', tpl: 'KSh {{amount}}' },
    GHS: { sym: 'GH₵ ', code: 'GHS', tpl: 'GH₵{{amount}}' },
    MXN: { sym: '$', code: 'MXN', tpl: '${{amount}}' },
    BRL: { sym: 'R$ ', code: 'BRL', tpl: 'R$ {{amount_with_comma_separator}}' },
    ARS: { sym: '$', code: 'ARS', tpl: '${{amount_with_comma_separator}}' },
    CLP: { sym: '$', code: 'CLP', tpl: '${{amount_no_decimals_with_comma_separator}}' },
    COP: { sym: '$', code: 'COP', tpl: '${{amount_with_comma_separator}}' },
    PEN: { sym: 'S/ ', code: 'PEN', tpl: 'S/ {{amount}}' },
    UYU: { sym: '$', code: 'UYU', tpl: '${{amount_with_comma_separator}}' },
    DOP: { sym: 'RD$', code: 'DOP', tpl: 'RD${{amount}}' },
    CRC: { sym: '₡', code: 'CRC', tpl: '₡{{amount_with_comma_separator}}' },
    GTQ: { sym: 'Q', code: 'GTQ', tpl: 'Q{{amount}}' },
    BOB: { sym: 'Bs ', code: 'BOB', tpl: 'Bs {{amount_with_comma_separator}}' },
    PYG: { sym: '₲ ', code: 'PYG', tpl: '₲{{amount_no_decimals_with_comma_separator}}' },
    ISK: { sym: 'kr ', code: 'ISK', tpl: '{{amount_no_decimals}} kr' },
    HRK: { sym: 'kn ', code: 'HRK', tpl: '{{amount_with_comma_separator}} kn' },
    RSD: { sym: 'РСД ', code: 'RSD', tpl: '{{amount_with_comma_separator}} РСД' },
    MKD: { sym: 'ден ', code: 'MKD', tpl: 'ден {{amount_with_comma_separator}}' },
    BAM: { sym: 'KM ', code: 'BAM', tpl: 'KM {{amount_with_comma_separator}}' },
    ALL: { sym: 'Lek ', code: 'ALL', tpl: 'Lek {{amount}}' },
    BYN: { sym: 'Br ', code: 'BYN', tpl: 'Br {{amount}}' },
    KZT: { sym: '₸ ', code: 'KZT', tpl: '{{amount}} ₸' },
    UZS: { sym: 'UZS ', code: 'UZS', tpl: '{{amount}} UZS' },
    GEL: { sym: '₾ ', code: 'GEL', tpl: '{{amount}} ₾' }
  };

  /* ---------- Country → currency mapping ---------- */
  var COUNTRY_CURRENCY = {
    US: 'USD', CA: 'CAD', GB: 'GBP', AU: 'AUD', NZ: 'NZD',
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR', MT: 'EUR', CY: 'EUR', HR: 'EUR',
    CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN', IS: 'ISK', RS: 'RSD', MK: 'MKD', BA: 'BAM', AL: 'ALL', BY: 'BYN',
    RU: 'RUB', UA: 'UAH', KZ: 'KZT', UZ: 'UZS', GE: 'GEL', TR: 'TRY',
    JP: 'JPY', CN: 'CNY', HK: 'HKD', TW: 'TWD', KR: 'KRW', SG: 'SGD', TH: 'THB', MY: 'MYR', ID: 'IDR', PH: 'PHP', VN: 'VND', IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR',
    AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', JO: 'JOD', IL: 'ILS', EG: 'EGP', MA: 'MAD',
    ZA: 'ZAR', NG: 'NGN', KE: 'KES', GH: 'GHS',
    MX: 'MXN', BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', UY: 'UYU', DO: 'DOP', CR: 'CRC', GT: 'GTQ', BO: 'BOB', PY: 'PYG', PR: 'USD'
  };

  /* ---------- Helpers ---------- */
  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function writeCookie(name, value, days) {
    var d = new Date(); d.setTime(d.getTime() + (days || 30) * 86400000);
    document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (precision == null) precision = 2;
    if (thousands == null) thousands = ',';
    if (decimal == null) decimal = '.';
    if (isNaN(cents) || cents == null) return '0';
    var n = (cents / 100).toFixed(precision);
    var parts = n.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var c = parts[1] ? (decimal + parts[1]) : '';
    return dollars + c;
  }

  function formatMoney(cents, fmtTpl, currencyCode) {
    var match = fmtTpl.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmtTpl;
    var placeholder = match[1];
    var decimals = 2;
    if (currencyCode === 'JPY' || currencyCode === 'KRW' || currencyCode === 'IDR' || currencyCode === 'VND' || currencyCode === 'CLP' || currencyCode === 'PYG' || currencyCode === 'ISK' || currencyCode === 'HUF') decimals = 0;
    if (currencyCode === 'KWD' || currencyCode === 'BHD' || currencyCode === 'JOD' || currencyCode === 'OMR') decimals = 3;
    if (REMOVE_DECIMALS) decimals = 0;

    var value;
    switch (placeholder) {
      case 'amount':
        value = formatWithDelimiters(cents, decimals);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, decimals, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, decimals);
    }
    if (ROUND_DECIMALS && currencyCode !== SHOP && decimals > 0 && cents > 0) {
      var dec = (placeholder.indexOf('comma') !== -1) ? ',' : '.';
      var idx = value.indexOf(dec);
      if (idx !== -1) value = value.substr(0, idx + 1) + ROUND_TO;
    }
    var out = fmtTpl.replace(/\{\{\s*\w+\s*\}\}/, value);
    if (SHOW_CODE && out.indexOf(currencyCode) === -1) out += ' ' + currencyCode;
    return out;
  }

  function convert(centsInShopCurrency, toCurrency) {
    if (!window.Currency || !window.Currency.rates) return centsInShopCurrency;
    var rates = window.Currency.rates;
    if (!rates[SHOP] || !rates[toCurrency]) return centsInShopCurrency;
    return Math.round(centsInShopCurrency * rates[SHOP] / rates[toCurrency]);
  }

  function getFormat(code) {
    return FORMATS[code] || { sym: code + ' ', code: code, tpl: '{{amount}} ' + code };
  }

  /* ---------- Price element parsing ---------- */
  function parseCents(text, currencyCode) {
    if (!text) return 0;
    var raw = String(text).replace(/<sup>/g, '.').replace(/<\/sup>/g, '');
    var hasDecimal = raw.indexOf('.') !== -1 || raw.indexOf(',') !== -1;
    var digits = raw.replace(/[^0-9]/g, '');
    if (!digits) return 0;
    if (!hasDecimal) {
      if (currencyCode === 'JPY' || currencyCode === 'KRW' || currencyCode === 'CLP' || currencyCode === 'IDR' || currencyCode === 'VND' || currencyCode === 'PYG' || currencyCode === 'ISK' || currencyCode === 'HUF') {
        return parseInt(digits, 10) * 100;
      }
      return parseInt(digits, 10) * 100;
    }
    if (currencyCode === 'KWD' || currencyCode === 'BHD' || currencyCode === 'JOD' || currencyCode === 'OMR') {
      return Math.round(parseInt(digits, 10) / 10);
    }
    return parseInt(digits, 10);
  }

  function convertAll(toCurrency) {
    toCurrency = (toCurrency || SHOP).toUpperCase();
    if (ALLOWED.indexOf(toCurrency) === -1) toCurrency = SHOP;
    var els = document.querySelectorAll('span.' + SPAN_CLASS);
    var fmt = getFormat(toCurrency).tpl;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.getAttribute('data-ncc-currency') === toCurrency) continue;
      var savedAttr = el.getAttribute('data-ncc-' + toCurrency);
      var cents;
      if (savedAttr) {
        cents = parseInt(savedAttr, 10);
      } else {
        var shopCents = el.getAttribute('data-ncc-' + SHOP);
        if (!shopCents) {
          shopCents = parseCents(el.textContent, SHOP);
          el.setAttribute('data-ncc-' + SHOP, shopCents);
        } else {
          shopCents = parseInt(shopCents, 10);
        }
        cents = (toCurrency === SHOP) ? shopCents : convert(shopCents, toCurrency);
        el.setAttribute('data-ncc-' + toCurrency, cents);
      }
      el.innerHTML = formatMoney(cents, fmt, toCurrency);
      el.setAttribute('data-ncc-currency', toCurrency);
    }
    updateExtras(toCurrency);
  }

  /* ---------- Switcher UI ---------- */
  function buildSwitcher(currentCurrency) {
    var wrapper = document.querySelector('.ncc-wrapper');
    if (!wrapper) return;
    if (HIDE_SWITCHER) { wrapper.style.display = 'none'; return; }
    if (wrapper.dataset.built === '1') return;
    wrapper.dataset.built = '1';
    var sel = document.createElement('select');
    sel.className = 'ncc-select';
    sel.setAttribute('aria-label', 'Currency');
    for (var i = 0; i < ALLOWED.length; i++) {
      var c = ALLOWED[i];
      var opt = document.createElement('option');
      opt.value = c;
      opt.textContent = c;
      if (c === currentCurrency) opt.selected = true;
      sel.appendChild(opt);
    }
    sel.addEventListener('change', function () {
      var v = sel.value;
      writeCookie(COOKIE_NAME, v);
      convertAll(v);
    });
    wrapper.appendChild(sel);
  }

  /* ---------- Extras (checkout notice, price-on-hover) ---------- */
  function updateExtras(currentCurrency) {
    /* Checkout notice */
    if (SHOW_NOTICE) {
      var notice = document.querySelector('.ncc-checkout-notice');
      if (notice) {
        if (currentCurrency === SHOP) {
          notice.style.display = 'none';
        } else {
          notice.style.display = '';
          notice.innerHTML = NOTICE_TEXT.replace(/\[shop_currency\]/g, SHOP);
        }
      }
    }
    /* Price-on-hover removes any old tooltips before adding */
    if (SHOW_PRICE_ON_HOVER) {
      var spans = document.querySelectorAll('span.' + SPAN_CLASS);
      for (var i = 0; i < spans.length; i++) {
        var s = spans[i];
        if (s.dataset.nccHover === '1') continue;
        s.dataset.nccHover = '1';
        s.style.position = s.style.position || 'relative';
        s.addEventListener('mouseenter', showHover);
        s.addEventListener('mouseleave', hideHover);
      }
    }
  }
  function showHover(e) {
    if (!window.__NCC_CURRENT || window.__NCC_CURRENT === SHOP) return;
    var t = e.currentTarget;
    var shopCents = parseInt(t.getAttribute('data-ncc-' + SHOP) || '0', 10);
    if (!shopCents) return;
    var tip = document.createElement('span');
    tip.className = 'ncc-hover-tip';
    tip.textContent = formatMoney(shopCents, getFormat(SHOP).tpl, SHOP);
    t.appendChild(tip);
  }
  function hideHover(e) {
    var tip = e.currentTarget.querySelector('.ncc-hover-tip');
    if (tip) tip.remove();
  }

  /* ---------- Country detection ---------- */
  function detectCountry(callback) {
    if (CONFIG.shopifyCountry) { callback((CONFIG.shopifyCountry || '').toUpperCase()); return; }
    if (!AUTO_DETECT) { callback(null); return; }
    try {
      fetch(GEO_ENDPOINT, { cache: 'force-cache' })
        .then(function (r) { return r.json(); })
        .then(function (data) { callback((data.country_code || data.country || '').toUpperCase()); })
        .catch(function () { callback(null); });
    } catch (e) { callback(null); }
  }

  /* ---------- Boot ---------- */
  function boot() {
    var cookieCur = readCookie(COOKIE_NAME);
    if (cookieCur && ALLOWED.indexOf(cookieCur.toUpperCase()) !== -1) {
      var c = cookieCur.toUpperCase();
      window.__NCC_CURRENT = c;
      buildSwitcher(c);
      convertAll(c);
      observeMutations();
      return;
    }
    detectCountry(function (cc) {
      var cur = SHOP;
      if (cc && COUNTRY_CURRENCY[cc] && ALLOWED.indexOf(COUNTRY_CURRENCY[cc]) !== -1) {
        cur = COUNTRY_CURRENCY[cc];
      }
      window.__NCC_CURRENT = cur;
      writeCookie(COOKIE_NAME, cur);
      buildSwitcher(cur);
      convertAll(cur);
      observeMutations();
    });
  }

  /* Observe dynamically-injected prices (cart drawer, ajax updates) */
  function observeMutations() {
    var debounceTimer;
    var observer = new MutationObserver(function (mutations) {
      var hasMoney = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType === 1 && (n.classList && n.classList.contains(SPAN_CLASS) || (n.querySelector && n.querySelector('span.' + SPAN_CLASS)))) {
            hasMoney = true; break;
          }
        }
        if (hasMoney) break;
      }
      if (hasMoney) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () {
          convertAll(window.__NCC_CURRENT || SHOP);
        }, 100);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
